import * as React from 'react';
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import InfoIcon from "components/UI/InfoIcon";
import RenderTag from "components/UI/RenderTag";
import Brand from "data/Brand";
import Keyword from "data/Keyword";
import { BorderRadius, Color, FontSize, FontWeight, Size } from "lib/styles";
import styled, { css } from "styled-components";
import AutosuggestRenderInput from "view/Autosuggest";
import EditorSection from "view/EditorSection";
import RichTextEditor from "view/RichTextEditor";
import TagsInputComponent from "view/TagsInputComponent";
import { BRAND_CONSTANTS } from 'constants/brands';
import { validateBrandSEODescription, validateBrandSEOTitle } from 'utils/brand';
import InputComponent from 'components/UI/Input';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';

const Heading = styled.h1`
  font-size: ${FontSize.large};
  font-weight: ${FontWeight.medium};
`;

const StyledInfoIcon = styled(InfoIcon)`
  display: flex;
  margin-left: ${Size.small};
  margin-top: 0.1rem;
`;

const ErrorMessage = styled.p`
    color: ${Color.error};
    font-size: 12px;
    margin-top: 2px;
`

const InputContainer = styled.div<any>`
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid ${Color.veryLightGray};
  border-radius: ${BorderRadius.medium};
  width: 100%;
  padding-right: 5px;
  margin-top: 10px;

  ${({ disabled }) => disabled && css`
    pointer-events: none;
   background-color: #E9ECEF;
  `}
`;

interface Props {
    brand: Brand;
    setBrand: (brand: Brand) => void;
    errors: any;
    setErrors: any;
    keywordsList: Keyword[];
    hasEditRights?: boolean;
    keywordsTags: string[];
    setKeywordsTags: (keywords: string[]) => void;
    onSeoTitleBlur: (value: string) => void;
    onSeoDescBlur: (value: string) => void;
};

const BrandSEOConfig = ({ brand, setBrand,
    errors, keywordsList, hasEditRights, setKeywordsTags,
    keywordsTags, onSeoTitleBlur, onSeoDescBlur, setErrors
}: Props) => {

    const { readOnly: isAddSEOReadOnly } = useReadPermission(PermissionCode.BRANDS_ADD_SEO);
    
    const onKeywordsChange = (keywords: string[]) => {
        setKeywordsTags(keywords);
        setBrand({ ...brand, keywords });
    };

    return (
        <FlexboxContainerComponent flexDirection='column' padding='10px 24px'>
            <FlexboxContainerComponent alignItems='center'>
                <Heading>{BRAND_CONSTANTS.SEO}</Heading>
                <StyledInfoIcon
                    productRedesignFlag
                    tooltipText={BRAND_CONSTANTS.SEO_OPTIMIZATION_DESCRIPTION}
                    size={14}
                />
            </FlexboxContainerComponent>

            <EditorSection
                shouldShowValidationStatus={false}
                noBorder={true}
                label="SEO title"
                isOptional={true}
                disablePaddingBottom={true}
                productRedesignFlag
                isInvalid={!!errors.seoTitle}
            >
                <InputComponent
                    id='brand-seo-title-toolbar'
                    placeholder={BRAND_CONSTANTS.ENTER_SEO_TITLE}
                    value={brand.seoTitle || ''}
                    width="100%"
                    name="brand-seo-title"
                    onInputChange={(seoTitle) => {
                        const _errors = validateBrandSEOTitle(seoTitle);
                        if (_errors.seoTitle) {
                            setErrors({ ...errors, ..._errors });
                        } else {
                            setErrors({ ...errors, seoTitle: '' });
                        }
                        setBrand({ ...brand, seoTitle })
                    }
                    }
                    productRedesignFlag
                    isInvalid={!!errors.seoTitle}
                    errorMessage={errors.seoTitle}
                    disabled={!hasEditRights}
                    readOnly={isAddSEOReadOnly}
                />

            </EditorSection>

            <EditorSection
                shouldShowValidationStatus={false}
                noBorder={true}
                label="SEO description"
                isOptional={true}
                disablePaddingBottom={true}
                productRedesignFlag
                isInvalid={!!errors.seoDescription}
            >
                <InputComponent
                    id='brand-seo-desc-toolbar'
                    placeholder={BRAND_CONSTANTS.ENTER_SEO_DESCRIPTION}
                    value={brand.seoDescription || ''}
                    width="100%"
                    name="brand-seo-description"
                    onInputChange={(seoDescription) => {
                        const _errors = validateBrandSEODescription(seoDescription);
                        if (_errors.seoDescription) {
                            setErrors({ ...errors, ..._errors });
                        } else {
                            setErrors({ ...errors, seoDescription: '' });
                        }
                        setBrand({ ...brand, seoDescription })
                    }
                    }
                    productRedesignFlag
                    isInvalid={!!errors.seoDescription}
                    errorMessage={errors.seoDescription}
                    disabled={!hasEditRights}
                    readOnly={isAddSEOReadOnly}
                />

            </EditorSection>

            <EditorSection
                noBorder={true}
                label={BRAND_CONSTANTS.KEYWORDS}
                disablePaddingBottom={true}
            >
                <FlexboxContainerComponent flexDirection="column" maxWidth='50%' margin='0px 0px 40px 0px'>
                    <InputContainer disabled={!hasEditRights}>
                        <TagsInputComponent
                            value={keywordsTags}
                            onChange={onKeywordsChange}
                            inputProps={{
                                className: 'react-tagsinput-input',
                                placeholder: keywordsTags.length === 0 ? 'Enter brand keyword(s)' : '',
                                keywords: keywordsList,
                            }}
                            tagProps={{ className: 'react-tagsinput-tag-custom' }}
                            renderTag={RenderTag}
                            renderInput={AutosuggestRenderInput}
                            disabled={!hasEditRights}
                            permissionCode={PermissionCode.BRANDS_ADD_SEO}
                        />
                    </InputContainer>
                </FlexboxContainerComponent>
            </EditorSection>

        </FlexboxContainerComponent>);
}

export default BrandSEOConfig;